import React, { useContext, useState, useRef, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { US_STATES } from '../constants/states';

function Navbar({ onLoginClick, onProfileClick, onStateSelect, onReviewsClick, darkMode, onToggleDarkMode }) {
  const { user, logout } = useContext(AuthContext);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileStatesOpen, setMobileStatesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setStateDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileStatesOpen(false);
  };

  return (
    <header className="relative z-40 border-b border-slate-200 bg-white/90 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/90">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:gap-6 sm:px-6 sm:py-4 lg:px-8">
        {/* Logo */}
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <button
            type="button"
            onClick={onReviewsClick}
            aria-label="Go to homepage"
            className="rounded-2xl transition hover:bg-slate-100/80 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:hover:bg-slate-800/70 dark:focus:ring-slate-600"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-200/50 dark:bg-slate-200 dark:text-slate-900 dark:shadow-none sm:h-12 sm:w-12 sm:rounded-3xl">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 sm:h-6 sm:w-6">
                <path d="M3.75 6.75a.75.75 0 0 0-.75.75v10.5c0 .414.336.75.75.75h.75a3 3 0 0 0 5.86.75h3.28a3 3 0 0 0 5.86-.75h.75a.75.75 0 0 0 .75-.75V7.5a.75.75 0 0 0-.75-.75H3.75Zm.75 9.75V8.25h15v8.25h-.405a3.002 3.002 0 0 0-5.69 0H9.595a3.002 3.002 0 0 0-5.69 0H4.5Z"/>
                <path d="M6 10.5a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5A.75.75 0 0 1 6 10.5ZM7.5 15a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm9 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/>
              </svg>
            </div>
          </button>
          <div className="min-w-0 text-left">
            <p className="truncate text-base font-semibold text-slate-900 dark:text-slate-100 sm:text-lg">RateThatDriver</p>
            <p className="hidden truncate text-xs text-slate-500 dark:text-slate-400 sm:block sm:text-sm">Report and track bad drivers near you</p>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Desktop browse buttons */}
          <div className="hidden sm:flex items-center gap-2">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setStateDropdownOpen((o) => !o)}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                  <path fillRule="evenodd" d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75Zm0 5A.75.75 0 0 1 2.75 9h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 9.75Zm0 5a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                </svg>
                States
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={`h-4 w-4 transition-transform ${stateDropdownOpen ? 'rotate-180' : ''}`}>
                  <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {stateDropdownOpen && (
                <div className="absolute left-0 top-full z-[60] mt-2 max-h-72 w-56 overflow-y-auto rounded-2xl border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:shadow-none">
                  {US_STATES.map((s) => (
                    <button
                      key={s.code}
                      onClick={() => { onStateSelect(s.code); setStateDropdownOpen(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <span className="font-semibold">{s.code}</span> — {s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={onReviewsClick}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                <path fillRule="evenodd" d="M2 10c0-4.418 3.582-8 8-8s8 3.582 8 8c0 1.87-.643 3.59-1.714 4.952l1.207 2.414a.75.75 0 0 1-.928.997l-3.076-.923A7.956 7.956 0 0 1 10 18c-4.418 0-8-3.582-8-8Zm8-3a.75.75 0 0 0 0 1.5h.01a.75.75 0 0 0 0-1.5H10Zm-1.25 3.75a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-1.75H9a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
              </svg>
              Reviews
            </button>
          </div>

          {/* Dark mode toggle — always visible */}
          <button
            type="button"
            onClick={onToggleDarkMode}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            className="inline-flex items-center justify-center rounded-2xl bg-slate-100 p-2.5 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            {darkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                <path d="M12 2.25a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0V3a.75.75 0 0 1 .75-.75ZM12 18.75a.75.75 0 0 1 .75.75V21a.75.75 0 0 1-1.5 0v-1.5a.75.75 0 0 1 .75-.75ZM4.72 4.72a.75.75 0 0 1 1.06 0l1.06 1.06a.75.75 0 0 1-1.06 1.06L4.72 5.78a.75.75 0 0 1 0-1.06ZM17.16 17.16a.75.75 0 0 1 1.06 0l1.06 1.06a.75.75 0 1 1-1.06 1.06l-1.06-1.06a.75.75 0 0 1 0-1.06ZM2.25 12a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5H3a.75.75 0 0 1-.75-.75ZM18.75 12a.75.75 0 0 1 .75-.75H21a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75ZM5.78 17.16a.75.75 0 0 1 1.06 1.06l-1.06 1.06a.75.75 0 1 1-1.06-1.06l1.06-1.06ZM18.22 4.72a.75.75 0 0 1 1.06 1.06l-1.06 1.06a.75.75 0 0 1-1.06-1.06l1.06-1.06ZM12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 0 1 .162.814 9 9 0 0 0 11.794 11.794.75.75 0 0 1 .976.976A10.5 10.5 0 1 1 8.552 1.394a.75.75 0 0 1 .976.324Z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          {/* Sign in button — always visible on mobile */}
          {!user && (
            <button
              onClick={onLoginClick}
              className="rounded-2xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 sm:px-4"
            >
              Sign in
            </button>
          )}

          {/* Desktop: signed-in user controls */}
          {user && (
            <div className="hidden sm:flex items-center gap-3 border-l border-slate-200 pl-4 dark:border-slate-700">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{user.email}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Signed in</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onProfileClick}
                  className="rounded-3xl bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                >
                  My Account
                </button>
                <button
                  onClick={logout}
                  className="rounded-3xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* Mobile: My Account icon (when signed in) */}
          {user && (
            <button
              onClick={onProfileClick}
              className="flex sm:hidden items-center justify-center rounded-2xl bg-slate-200 p-2.5 text-slate-700 transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100"
              aria-label="My Account"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" />
              </svg>
            </button>
          )}

          {/* Hamburger — mobile only */}
          <div className="relative sm:hidden" ref={mobileMenuRef}>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label="Open menu"
              className="inline-flex items-center justify-center rounded-2xl bg-slate-100 p-2.5 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100"
            >
              {mobileMenuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path fillRule="evenodd" d="M3 6.75A.75.75 0 0 1 3.75 6h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 6.75ZM3 12a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 12Zm0 5.25a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* Mobile dropdown menu */}
            {mobileMenuOpen && (
              <div className="absolute right-0 top-full z-[60] mt-2 w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
                {/* Reviews */}
                <button
                  onClick={() => { onReviewsClick(); closeMobileMenu(); }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
                    <path fillRule="evenodd" d="M2 10c0-4.418 3.582-8 8-8s8 3.582 8 8c0 1.87-.643 3.59-1.714 4.952l1.207 2.414a.75.75 0 0 1-.928.997l-3.076-.923A7.956 7.956 0 0 1 10 18c-4.418 0-8-3.582-8-8Zm8-3a.75.75 0 0 0 0 1.5h.01a.75.75 0 0 0 0-1.5H10Zm-1.25 3.75a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-1.75H9a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                  </svg>
                  All Reviews
                </button>

                {/* States accordion */}
                <button
                  onClick={() => setMobileStatesOpen((o) => !o)}
                  className="flex w-full items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <span className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
                      <path fillRule="evenodd" d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75Zm0 5A.75.75 0 0 1 2.75 9h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 9.75Zm0 5a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                    </svg>
                    Browse by State
                  </span>
                  <svg viewBox="0 0 20 20" fill="none" className={`h-4 w-4 transition-transform ${mobileStatesOpen ? 'rotate-180' : ''}`}>
                    <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {mobileStatesOpen && (
                  <div className="max-h-52 overflow-y-auto border-t border-slate-100 dark:border-slate-800">
                    {US_STATES.map((s) => (
                      <button
                        key={s.code}
                        onClick={() => { onStateSelect(s.code); closeMobileMenu(); }}
                        className="w-full px-5 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                      >
                        <span className="font-semibold">{s.code}</span> — {s.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Signed-in user actions on mobile */}
                {user && (
                  <>
                    <div className="border-t border-slate-100 px-4 py-2 dark:border-slate-800">
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                    </div>
                    <button
                      onClick={() => { logout(); closeMobileMenu(); }}
                      className="flex w-full items-center gap-3 border-t border-slate-100 px-4 py-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 dark:border-slate-800 dark:hover:bg-slate-800"
                    >
                      Logout
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default React.memo(Navbar);
